/**
 * StaffDetailSidebar (11F.7) — the Staff detail left rail (ref `staff1.png`).
 *
 * Mirrors the Student rail's structure on the same shared primitives. The
 * role chip reads from `staff_type` (Instructor / Office / Other), which is
 * the classification the nav and dashboards key on — deliberately NOT the
 * RBAC role, since "admin" describes permissions, not what the person does.
 *
 * "Takes classes" is derived, not stored: a staff member takes classes when
 * they are classified as a teacher. There is no separate toggle field on the
 * schema, so this is presented as read-only state rather than a control that
 * would silently do nothing (`staff1.png` shows it as a toggle — logged as a
 * scope gap in docs/04-modules/people.md).
 */

import React from 'react';
import { Badge } from '@/components/common/Badge';
import {
  RailCard,
  RailField,
  RailFields,
  RailHeading,
  RailInsight,
  RailInsights,
  RailQuickActions,
  RailSection,
  type QuickAction,
} from '@/components/common';
import type { StaffResponse } from '@features/people/api/staff';
import { formatTenure, getFullName } from '@/utils';

const STAFF_TYPE_LABEL: Record<string, string> = {
  teacher: 'Instructor',
  office_manager: 'Office',
  other: 'Other',
};

function telHref(phone: string | null | undefined, scheme: 'tel' | 'sms'): string | undefined {
  if (!phone) return undefined;
  const cleaned = phone.replace(/[^\d+]/g, '');
  return cleaned ? `${scheme}:${cleaned}` : undefined;
}

function whatsappHref(phone: string | null | undefined): string | undefined {
  if (!phone) return undefined;
  const digits = phone.replace(/\D/g, '');
  return digits ? `https://wa.me/${digits}` : undefined;
}

interface Props {
  staff: StaffResponse;
  /** Cross-feature insight cells (classes, students) injected by routes. */
  renderInsights?: (staffId: string) => React.ReactNode;
}

export const StaffDetailSidebar: React.FC<Props> = ({ staff, renderInsights }) => {
  const tenure = formatTenure(staff.hire_date ?? staff.created_at);
  const staffType = staff.staff_type ?? null;
  const takesClasses = staffType === 'teacher';

  const actions: QuickAction[] = [
    { key: 'call', label: 'Call', href: telHref(staff.phone, 'tel') },
    { key: 'message', label: 'Message', href: telHref(staff.phone, 'sms') },
    { key: 'whatsapp', label: 'WhatsApp', href: whatsappHref(staff.phone) },
  ];

  return (
    <>
      <RailCard>
        <RailQuickActions actions={actions} testId="candeza-staff-rail-actions" />
      </RailCard>

      <RailCard data-testid="candeza-staff-rail-insights">
        <RailHeading>Insights</RailHeading>
        <RailInsights>
          {renderInsights ? (
            renderInsights(staff.id)
          ) : (
            <RailInsight label="Classes" value="—" />
          )}
          <RailInsight
            label="With us"
            value={tenure ?? '—'}
            testId="candeza-staff-rail-tenure"
          />
        </RailInsights>
      </RailCard>

      <RailCard>
        <RailHeading>Personal information</RailHeading>
        <RailFields>
          <RailField label="Name" value={getFullName(staff.first_name, staff.last_name)} />
          <RailField label="Email" value={staff.email} />
          <RailField label="Phone" value={staff.phone} />
          <RailField
            label="Classification"
            value={
              staffType ? (
                <Badge variant="primary" size="sm">
                  {STAFF_TYPE_LABEL[staffType] ?? staffType}
                </Badge>
              ) : (
                null
              )
            }
            testId="candeza-staff-rail-type"
          />
          <RailField
            label="Takes classes"
            value={takesClasses ? 'Yes' : 'No'}
            testId="candeza-staff-rail-takes-classes"
          />
          <RailField label="Access role" value={staff.role} />
          <RailField label="Hired" value={staff.hire_date} />
        </RailFields>
      </RailCard>

      {staff.bio && (
        <RailSection title="Biography" defaultOpen testId="candeza-staff-rail-bio">
          <p style={{ fontSize: 14, lineHeight: 1.6, color: '#334155', margin: 0 }}>
            {staff.bio}
          </p>
        </RailSection>
      )}

      {(staff.expertise ?? []).length > 0 && (
        <RailSection title="Expertise" testId="candeza-staff-rail-expertise">
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {(staff.expertise ?? []).map((tag) => (
              <Badge key={tag} variant="secondary" size="sm">
                {tag}
              </Badge>
            ))}
          </div>
        </RailSection>
      )}
    </>
  );
};

export default StaffDetailSidebar;
